import { rootId } from '@griply/types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LifeAreaMultiSelectContextMenu } from 'shared/components/connected/life-area-multi-select-context-menu';
import { TreeSectionHeader } from 'shared/components/ui/tree-section-header';
import {
  RenderItemProps,
  TaskListItemTypes,
  TreeItem,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import { buildLifeAreaTreeItems } from '../../build-life-area-tree-items';
import { LIFE_AREA_ARCHIVED_SECTION_ID } from '../../constants';
import { LifeAreaRowData } from '../../types';
import { MenuPosition, TreeLifeAreaCard } from '../tree-life-area-card';
import * as Styled from './life-area-list.style';

// Life areas never nest — only the root takes drops.
const dropTypes: Record<TaskListItemTypes, TaskListItemTypes[]> = {
  [TaskListItemTypes.Root]: [TaskListItemTypes.LifeArea],
  [TaskListItemTypes.LifeArea]: [],
  [TaskListItemTypes.Section]: [],
  [TaskListItemTypes.Goal]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.GoalDeadline]: [],
};

const dragTypes = [TaskListItemTypes.LifeArea];
const noDragTypes: TaskListItemTypes[] = [];
// The archived section header is not selectable — only rows are.
const multiselectTypes = [TaskListItemTypes.LifeArea];
const initialCollapsedItems = [LIFE_AREA_ARCHIVED_SECTION_ID];

type MultiSelectMenu = {
  position: MenuPosition;
  lifeAreas: LifeArea[];
};

export type LifeAreaListProps = {
  rows: LifeAreaRowData[];
  archivedRows?: LifeAreaRowData[];
  scrollElement?: React.RefObject<HTMLDivElement>;
  onReorder?: (ids: ID[]) => void;
};

export const LifeAreaList: React.FC<LifeAreaListProps> = ({
  rows,
  archivedRows,
  scrollElement,
  onReorder,
}) => {
  const { t } = useTranslation();
  const [collapsedItems, setCollapsedItems] = useState<ID[]>(
    initialCollapsedItems,
  );
  const [multiSelectMenu, setMultiSelectMenu] = useState<MultiSelectMenu>();

  const treeItems = useMemo(
    () => buildLifeAreaTreeItems(rows, archivedRows),
    [rows, archivedRows],
  );

  const rowsById = useMemo(
    () =>
      [...rows, ...(archivedRows ?? [])].reduce<Record<ID, LifeAreaRowData>>(
        (acc, row) => {
          acc[row.id] = row;
          return acc;
        },
        {},
      ),
    [rows, archivedRows],
  );

  const archivedIds = useMemo(
    () => new Set((archivedRows ?? []).map(({ id }) => id)),
    [archivedRows],
  );

  // Archived rows share the LifeArea type, so the drag gate must be per-item.
  const isItemDraggable = useCallback(
    (item: TreeItem) =>
      item.type === TaskListItemTypes.LifeArea && !archivedIds.has(item.id),
    [archivedIds],
  );

  const handleReorder = useCallback(
    (updatedItems: TreeItem[]) => {
      const root = updatedItems.find(({ id }) => id === rootId);
      if (!root) {
        return;
      }
      onReorder?.(
        (root.children ?? []).filter(
          (id) => id !== LIFE_AREA_ARCHIVED_SECTION_ID,
        ),
      );
    },
    [onReorder],
  );

  const openMultiSelectMenu = useCallback(
    (position: MenuPosition, lifeAreas: LifeArea[]) =>
      setMultiSelectMenu({ position, lifeAreas }),
    [],
  );

  const closeMultiSelectMenu = useCallback(
    () => setMultiSelectMenu(undefined),
    [],
  );

  const renderItem = useCallback(
    ({ itemInstance, tree, isMultiSelectModifierPressed }: RenderItemProps) => {
      const sourceId = itemInstance.getItemData().sourceId;

      if (sourceId === LIFE_AREA_ARCHIVED_SECTION_ID) {
        return (
          <TreeSectionHeader
            item={itemInstance}
            lifeArea={undefined}
            overrideTitle={t('pages.life-areas.sections.archived.title')}
            overrideIcon={null}
            disableLink
            useSecondaryTitle
            isFirst={!rows.length}
          />
        );
      }

      const row = rowsById[sourceId];

      if (!row) {
        return null;
      }

      return (
        <TreeLifeAreaCard
          row={row}
          rowsById={rowsById}
          item={itemInstance}
          tree={tree}
          isArchived={archivedIds.has(row.id)}
          isMultiSelectModifierPressed={isMultiSelectModifierPressed}
          onOpenMultiSelectMenu={openMultiSelectMenu}
        />
      );
    },
    [rowsById, archivedIds, rows.length, t, openMultiSelectMenu],
  );

  if (!rows.length && !archivedRows?.length) {
    return null;
  }

  return (
    <Styled.Container>
      <VirtualizedTreeList
        items={treeItems}
        rootItemId={rootId}
        renderItem={renderItem}
        scrollElement={scrollElement}
        collapsedItems={collapsedItems}
        onCollapse={setCollapsedItems}
        onReorder={onReorder ? handleReorder : undefined}
        isItemDraggable={isItemDraggable}
        multiselectTypes={multiselectTypes}
        dropTypes={dropTypes}
        dragTypes={onReorder ? dragTypes : noDragTypes}
        isContextMenuOpen={!!multiSelectMenu}
        indentationWidth={2.5}
      />

      {!!multiSelectMenu && (
        <LifeAreaMultiSelectContextMenu
          lifeAreas={multiSelectMenu.lifeAreas}
          location={multiSelectMenu.position}
          position="bottom-start"
          onClose={closeMultiSelectMenu}
        />
      )}
    </Styled.Container>
  );
};
